import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Competition, CompetitionsService } from '../../../core/competitions/competitions';

/**
 * Edit/delete a competition — originally built for a site admin viewing a
 * competition they don't own (F-009 Milestone C, Slice 7), and reused
 * as-is for the owner's own self-service edit/delete (F-020): the backend's
 * `RequireCompetitionRole('OWNER')` guard on PATCH/DELETE already allows
 * both, so no new authorization logic was needed here, just a second entry
 * point in the parent page. Unlike PersonModal, the competition is already
 * loaded by the parent page, so this takes it as an input rather than
 * fetching its own copy.
 */
@Component({
  selector: 'app-competition-edit-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './competition-edit-modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './competition-edit-modal.css',
})
export class CompetitionEditModal implements OnInit {
  private readonly competitions = inject(CompetitionsService);
  private readonly router = inject(Router);

  readonly competition = input.required<Competition>();
  /** F-020 — where to send the viewer after a successful delete. The
   *  competition this page shows no longer exists once delete succeeds, so
   *  this can't just reload; the parent page passes the list appropriate
   *  to who's deleting (the owner's own competitions, or the admin list for
   *  a site admin managing someone else's). Defaults to the owner's list
   *  since that's the more common caller. */
  readonly redirectAfterDelete = input<string>('/competitions');
  readonly closed = output<void>();
  /** Emitted after a successful save, so the parent page reloads. Not
   *  emitted on delete — the page navigates away instead. */
  readonly changed = output<void>();

  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);

  readonly confirmingDelete = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  /** F-021 Milestone B — FREE's roster rules are fixed at creation and
   *  never owner-editable; the backend rejects a change attempt with a
   *  403, so the fields are disabled here rather than letting the owner
   *  hit that error after filling them in. `getRawValue()` still includes
   *  a disabled control's (unchanged) value, so save() naturally resends
   *  the same numbers rather than omitting them. */
  readonly rosterRulesLocked = computed(
    () => this.competition().ownerPlan === 'FREE',
  );

  readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true }),
    startDate: new FormControl('', { nonNullable: true }),
    endDate: new FormControl('', { nonNullable: true }),
    submissionEndDate: new FormControl('', { nonNullable: true }),
    maxEntries: new FormControl(0, { nonNullable: true }),
    maxAlternates: new FormControl(0, { nonNullable: true }),
    maxAge: new FormControl(0, { nonNullable: true }),
  });

  ngOnInit(): void {
    const c = this.competition();
    this.form.setValue({
      name: c.name,
      startDate: c.startDate.slice(0, 10),
      endDate: c.endDate.slice(0, 10),
      submissionEndDate: c.submissionEndDate.slice(0, 10),
      maxEntries: c.maxEntries,
      maxAlternates: c.maxAlternates,
      maxAge: c.maxAge,
    });

    if (this.rosterRulesLocked()) {
      this.form.controls.maxEntries.disable();
      this.form.controls.maxAlternates.disable();
      this.form.controls.maxAge.disable();
    }
  }

  close(): void {
    this.closed.emit();
  }

  async save(): Promise<void> {
    this.saving.set(true);
    this.saveError.set(null);
    const raw = this.form.getRawValue();
    try {
      await this.competitions.update(this.competition().id, {
        name: raw.name.trim(),
        startDate: raw.startDate,
        endDate: raw.endDate,
        submissionEndDate: raw.submissionEndDate,
        maxEntries: raw.maxEntries,
        maxAlternates: raw.maxAlternates,
        maxAge: raw.maxAge,
      });
      this.changed.emit();
      this.closed.emit();
    } catch (err) {
      this.saveError.set(this.errorMessageFor(err));
    } finally {
      this.saving.set(false);
    }
  }

  /** Navigates away instead of emitting `changed` — the competition this
   *  page shows no longer exists once this succeeds. */
  async confirmDelete(): Promise<void> {
    this.deleting.set(true);
    this.deleteError.set(null);
    try {
      await this.competitions.remove(this.competition().id);
      this.closed.emit();
      await this.router.navigateByUrl(this.redirectAfterDelete());
    } catch (err) {
      this.deleteError.set(this.errorMessageFor(err));
      this.confirmingDelete.set(false);
    } finally {
      this.deleting.set(false);
    }
  }

  private errorMessageFor(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 409) {
        return (
          (err.error as { message?: string } | null)?.message ??
          'This competition still has related data — remove that first.'
        );
      }
      // F-021 Milestone B — the roster-rule fields are disabled client-side
      // for a FREE-owned competition, so this only fires via direct API
      // misuse; still worth surfacing the backend's actual message.
      if (err.status === 403) {
        return (
          (err.error as { message?: string } | null)?.message ??
          "You don't have permission to make that change."
        );
      }
      if (err.status === 404) {
        return 'This competition no longer exists.';
      }
      if (err.status === 400) {
        return (
          (err.error as { message?: string } | null)?.message ??
          'Check the dates and try again.'
        );
      }
    }
    return 'Something went wrong — try again in a moment.';
  }
}
